import { useEffect, useState, useCallback } from "react";
import { Paperclip, Download, Trash2, FileText, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function Thumb({ att }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    let blobUrl;
    api.get(`/attachments/${att.id}/download`, { responseType: "blob" }).then((r) => {
      blobUrl = URL.createObjectURL(r.data);
      if (alive) setUrl(blobUrl);
    }).catch(() => {});
    return () => { alive = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [att.id]);
  if (!url) return <div className="h-10 w-10 rounded-lg bg-white/5 flex items-center justify-center"><ImageIcon className="h-4 w-4 text-zinc-500" /></div>;
  return <img src={url} alt={att.original_filename} className="h-10 w-10 rounded-lg object-cover" />;
}

export const Attachments = ({ taskId }) => {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => api.get(`/tasks/${taskId}/attachments`).then((r) => setItems(r.data)).catch(() => {}), [taskId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Máximo 10 MB"); return; }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/tasks/${taskId}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Archivo adjuntado");
      load();
    } catch { toast.error("No se pudo subir el archivo"); } finally { setUploading(false); }
  };

  const download = async (att) => {
    try {
      const r = await api.get(`/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar"); }
  };

  const remove = async (att) => {
    await api.delete(`/attachments/${att.id}`);
    load();
  };

  return (
    <div data-testid="attachments-section">
      <label className="text-xs uppercase tracking-wider font-semibold text-zinc-500 flex items-center gap-1.5">
        <Paperclip className="h-3 w-3" /> Adjuntos {items.length > 0 && `(${items.length})`}
      </label>
      <div className="space-y-1.5 mt-2">
        {items.map((att) => (
          <div key={att.id} className="flex items-center gap-3 bg-[#0A0A0B] rounded-lg px-3 py-2 group" data-testid={`attachment-${att.id}`}>
            {att.content_type?.startsWith("image/") ? <Thumb att={att} /> : (
              <div className="h-10 w-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-zinc-400" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {att.original_filename}
                {att.version > 1 && <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#00B0FF]/15 text-[#00B0FF]">v{att.version}</span>}
              </p>
              <p className="text-xs text-zinc-600">{fmtSize(att.size)} · {att.uploaded_by_name} · {new Date(att.created_at).toLocaleDateString("es", { day: "numeric", month: "short" })}</p>
            </div>
            <button onClick={() => download(att)} data-testid={`download-att-${att.id}`} className="text-zinc-500 hover:text-white"><Download className="h-4 w-4" /></button>
            <button onClick={() => remove(att)} data-testid={`delete-att-${att.id}`} className="opacity-0 group-hover:opacity-100 text-zinc-600 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <label className="flex items-center gap-2 text-sm text-zinc-500 hover:text-white border border-dashed border-white/10 hover:border-white/25 rounded-lg px-3 py-2 cursor-pointer transition-colors w-fit">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
          {uploading ? "Subiendo…" : "Adjuntar archivo"}
          <input type="file" data-testid="attachment-input" onChange={upload} className="hidden" disabled={uploading} />
        </label>
      </div>
    </div>
  );
};
